import React, { useState } from 'react';
import { Eye, EyeOff, CircleAlert } from 'lucide-react';
import { C, G, IconProp, resolveIcon } from './uiTokens';

interface GlobalInputFieldProps {
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  /** lucide component, element, or legacy "pi pi-xxx" string */
  icon?: IconProp;
  fullWidth?: boolean;
  autoComplete?: string;
  maxLength?: number;
  name?: string;
  id?: string;
  /** red border without a message (e.g. when the error is shown elsewhere) */
  invalid?: boolean;
  /** content aligned to the right of the label, e.g. a "Forgot password?" link */
  labelRight?: React.ReactNode;
  /** ref to the underlying <input> (focus management) */
  inputRef?: React.Ref<HTMLInputElement>;
  /** extra native input attributes (autoCapitalize, spellCheck, ...) */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

export const GlobalInputField: React.FC<GlobalInputFieldProps> = ({
  label,
  value,
  onChange,
  onBlur,
  onKeyDown,
  onKeyUp,
  placeholder = '',
  type = 'text',
  required = false,
  disabled = false,
  error = '',
  hint,
  icon,
  fullWidth = true,
  autoComplete,
  maxLength,
  name,
  id,
  invalid = false,
  labelRight,
  inputRef,
  inputProps,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword && showPassword ? 'text' : type;
  const hasIcon = !!icon;

  const hasError = !!error || invalid;
  const borderColor = hasError ? C.danger : focused ? 'rgba(17,85,80,0.5)' : G.line;

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '5.9px' }}>
          <label
            htmlFor={id}
            style={{ display: 'block', fontSize: '0.73rem', fontWeight: 600, color: error ? C.danger : '#374151' }}
          >
            {label}{required && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
          </label>
          {labelRight}
        </div>
      )}

      <div style={{ position: 'relative' }}>
        {hasIcon && (
          <span style={{ position: 'absolute', left: '11.8px', top: '50%', transform: 'translateY(-50%)', display: 'flex', pointerEvents: 'none', color: focused ? C.teal : C.muted, transition: 'color 0.18s' }}>
            {resolveIcon(icon, 15)}
          </span>
        )}

        <input
          {...inputProps}
          ref={inputRef}
          id={id}
          name={name}
          type={inputType}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onFocus={() => setFocused(true)}
          onBlur={e => { setFocused(false); onBlur?.(e); }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          maxLength={maxLength}
          aria-invalid={hasError}
          className="global-input-field"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            height: '38.7px',
            padding: `0 ${isPassword ? '38.7px' : '11.8px'} 0 ${hasIcon ? '35.5px' : '11.8px'}`,
            border: `1px solid ${borderColor}`,
            borderRadius: '8.9px',
            fontSize: '0.83rem',
            fontWeight: 500,
            fontFamily: 'inherit',
            color: disabled ? C.muted : '#1f2937',
            background: disabled ? '#f9fafb' : 'white',
            outline: 'none',
            boxShadow: focused ? `0 0 0 3px ${hasError ? 'rgba(220,38,38,0.1)' : 'rgba(17,85,80,0.08)'}` : 'none',
            transition: 'border-color 0.18s, box-shadow 0.18s',
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />

        {isPassword && (
          <button
            type="button"
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            onClick={() => !disabled && setShowPassword(s => !s)}
            style={{ position: 'absolute', right: '6.5px', top: '50%', transform: 'translateY(-50%)', width: '26.9px', height: '26.9px', border: 'none', background: 'transparent', color: C.muted, cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', padding: 0 }}
          >
            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        )}
      </div>

      {error ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4.3px', marginTop: '5.4px', fontSize: '0.71rem', color: C.danger, fontWeight: 500 }}>
          <CircleAlert size={12} /> {error}
        </span>
      ) : hint ? (
        <span style={{ display: 'block', marginTop: '5.4px', fontSize: '0.71rem', color: C.sub }}>{hint}</span>
      ) : null}

      <style>{`
        .global-input-field::placeholder { color: rgba(17,85,80,0.3); }
        .global-input-field:enabled:hover { border-color: rgba(17,85,80,0.3); }
      `}</style>
    </div>
  );
};